import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Spinner, Pagination, Container } from "react-bootstrap";
import { _get } from "../services/apiClient";
import { apiUrls } from "../config/apiUrls.const";
import ServiceItemGrid from "./components/ServiceItemGrid";
import MapModal from "./components/MapModal";
import { BookingForm, BookingFormRef } from "./components/BookingForm";

export default function GroupItems() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [items, setItems] = useState<any[]>([]);
  const [meta, setMeta] = useState<any>(null); // To store pagination info
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter States from URL
  const group = searchParams.get("service_group");
  const catId = searchParams.get("category_id");
  const subCatId = searchParams.get("subcategory_id");
  const page = searchParams.get("page") || "1";

  const bookingFormRef = useRef<BookingFormRef>(null);
  const [mapModalData, setMapModalData] = useState<any | null>(null);

  const fetchFilteredItems = async () => {
    setLoading(true);
    try {
      // Hits the paginated endpoint
      const response = await _get(apiUrls.serviceItemsSearch, {
        service_group: group,
        category_id: catId,
        subcategory_id: subCatId,
        page: page,
      });

      // Laravel API Resources return data in 'data' and pagination in 'meta'
      setItems(response.data.data);
      setMeta(response.data.meta);
    } catch (err: any) {
      setError("Could not load services.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilteredItems();
  }, [group, catId, subCatId, page]);

  const handlePageChange = (newPage: number) => {
    const currentParams = Object.fromEntries(searchParams);
    setSearchParams({ ...currentParams, page: newPage.toString() });
  };

  const handleItemClick = (id: any) => navigate(`/service-details?id=${id}`);
  const businessPage = (code: string) => navigate(`/webpage/${code}`);
  const handleMapModal = (lat: number, lon: number, address: string) => setMapModalData({ lat, lon, address });

  return (
    <Container className="py-4">
      {/* Dynamic Header */}
      <h1 className="page-header">
        {group ? `${group.charAt(0).toUpperCase() + group.slice(1)} Services` : "All Services"}
      </h1>
      <hr className="section-devider" />

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <>
          {/* REUSABLE GRID */}
          <ServiceItemGrid
            items={items}
            title={group || "Services"}
            handleItemClick={handleItemClick}
            businessPage={businessPage}
            handleMapModal={handleMapModal}
            bookingFormRef={bookingFormRef}
          />

          {/* BOOTSTRAP PAGINATION */}
          {meta && meta.last_page > 1 && (
            <Pagination className="justify-content-center mt-5">
              <Pagination.First onClick={() => handlePageChange(1)} disabled={meta.current_page === 1} />
              <Pagination.Prev onClick={() => handlePageChange(meta.current_page - 1)} disabled={meta.current_page === 1} />
              
              {[...Array(meta.last_page)].map((_, i) => (
                <Pagination.Item 
                  key={i + 1} 
                  active={i + 1 === meta.current_page}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              )).slice(Math.max(0, meta.current_page - 3), Math.min(meta.last_page, meta.current_page + 2))}

              <Pagination.Next onClick={() => handlePageChange(meta.current_page + 1)} disabled={meta.current_page === meta.last_page} />
              <Pagination.Last onClick={() => handlePageChange(meta.last_page)} disabled={meta.current_page === meta.last_page} />
            </Pagination>
          )}
        </>
      )}

      {/* Modals */}
      {mapModalData && (
        <MapModal 
          show={!!mapModalData} 
          onHide={() => setMapModalData(null)} 
          {...mapModalData} 
        />
      )}
      <BookingForm ref={bookingFormRef} onSuccess={() => {}} />
    </Container>
  );
}